import React, { useEffect, useState, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getListFincas, getAaffListSuplier, getDashboardCAE, getDashboardLOPD, getRiesgos, getSenalizaciones, getSalesStats, enviarEmailComercial, getSaleCaducidad, logContratacionModal } from "../../../../../services";
import { toast } from "react-toastify";
import { UsuarioContext } from "../../../../../providers/userContext";
import Logo from "../../../../../images/LogoLAENEW.png";
import Empty from "../../../../../icons/empty.png";
import AllDone from "../../../../../icons/alldone.png";
import { Building2, Wrench, Boxes, Loader2, Info, TriangleAlert, Zap, SignpostBig, X, ExternalLink, CalendarCheck, House } from "lucide-react";
import { Tooltip, Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import RadialBarMultiple from "../../../../../components/Charts/RadialBarMultiple";

function Inicio() {
  const { t, i18n } = useTranslation('common');
  const user = useContext(UsuarioContext);
  const navigate = useNavigate();
  const esCataluna = !!user?.esCataluna;
  const colFilaRiesgos = esCataluna ? "lg:w-1/3" : "lg:w-1/2";
  const [fincasCount, setFincasCount] = useState(0);
  const [proveedoresCount, setProveedoresCount] = useState(0);
  const [proveedoresVigentes, setProveedoresVigentes] = useState(0);
  const [reclamaciones, setReclamaciones] = useState([]);
  const [caeCount, setCaeCount] = useState(0);
  const [lopdCount, setLopdCount] = useState(0);
  const [visitsDone, setVisitsDone] = useState(0);
  const [visitsPending, setVisitsPending] = useState(0);
  const [visitsDoneBefore, setVisitsDoneBefore] = useState(0);
  const [caeRecords, setCaeRecords] = useState([]);
  const [lopdRecords, setLopdRecords] = useState([]);
  const [caeStatRecords, setCaeStatRecords] = useState([]);
  const [lopdStatRecords, setLopdStatRecords] = useState([]);
  const [caducidadRecords, setCaducidadRecords] = useState([]);
  const [loadingFincas, setLoadingFincas] = useState(true);
  const [loadingProveedores, setLoadingProveedores] = useState(true);
  const [loadingCae, setLoadingCae] = useState(true);
  const [loadingStatCae, setLoadingStatCae] = useState(true);
  const [loadingLopd, setLoadingLopd] = useState(true);
  const [loadingStatLopd, setLoadingStatLopd] = useState(true);
  const [loadingSaleCaducidad, setLoadingSaleCaducidad] = useState(true);
  const [riesgosData, setRiesgosData] = useState([]);
  const [senalizacionesData, setSenalizacionesData] = useState([]);
  const [riesgosSeriesData, setRiesgosSeriesData] = useState([]);
  const [senalesSeriesData, setSenalesSeriesData] = useState([]);
  const [loadingRiesgos, setLoadingRiesgos] = useState(true);
  const [loadingSenalizaciones, setLoadingSenalizaciones] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRisk, setModalRisk] = useState(null);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractType, setContractType] = useState('');
  const [contractSubject, setContractSubject] = useState('');
  const [contractContent, setContractContent] = useState('');
  const [sendingContract, setSendingContract] = useState(false);
  const [caducidadModalOpen, setCaducidadModalOpen] = useState(false);
  const caeSelectedIdx = useRef(null);
  const lopdSelectedIdx = useRef(null);
  const visitsSelectedIdx = useRef(null);

  // Top de señalizaciones y sus etiquetas, a nivel de componente para poder usarlas
  // tanto en el gráfico como al enviar el email comercial.
  const labelFor = (item) => t(`home.signalizationLabels.${item.statement_id}`, { defaultValue: item.pregunta });
  const topSenales = [...senalizacionesData]
    .sort((a, b) => (b.totalComunidades || 0) - (a.totalComunidades || 0))
    .slice(0, 6);
  const senalCategories = topSenales.map(labelFor);
  
  const senalizacionesConLabel = senalizacionesData.map((item) => ({
    ...item,
    label: labelFor(item),
  }));

  useEffect(() => {
    if (!user?.id) return;

    getListFincas({ filtros: {}, params: { page: 1, limit: 10 } })
      .then(res => setFincasCount(res?.meta?.total || 0))
      .catch(console.log)
      .finally(() => setLoadingFincas(false));

    getAaffListSuplier({ filtros: {}, params: { page: 1, limit: 9999 } })
      .then(res => {
        setProveedoresCount(res?.meta?.total || 0);
        const data = res?.data || [];
        const vigentes = data.filter(s => s.porcentajeDocs === 100).length;
        setProveedoresVigentes(vigentes);
        // Reclamaciones: proveedores con ultimaReclamacion, ordenados por fecha desc
        const reclam = data
          .filter(s => s.ultimaReclamacion)
          .sort((a, b) => new Date(b.ultimaReclamacion) - new Date(a.ultimaReclamacion))
          .slice(0, 5)
          .map(s => ({ name: s.razonSocial, nif: s.nif, date: new Date(s.ultimaReclamacion).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) }));
        setReclamaciones(reclam);
      })
      .catch(console.log)
      .finally(() => setLoadingProveedores(false));

    getDashboardCAE({ aaff_id: user.id })
      .then(res => {
        setCaeCount(res?.total || 0);
        setCaeRecords(res?.data || []);
        setVisitsDone(res?.visitsDone || 0);
        setVisitsPending(res?.visitsPending || 0);
        setVisitsDoneBefore(res?.visitsDoneBefore || 0);
      })
      .catch(console.log)
      .finally(() => setLoadingCae(false));

    getDashboardLOPD({ aaff_id: user.id })
      .then(res => {
        setLopdCount(res?.total || 0);
        setLopdRecords(res?.data || []);
      })
      .catch(console.log)
      .finally(() => setLoadingLopd(false));

      getSalesStats({ aaff_id: user.id, servp_ids: [1,3,6,7] })
      .then(res => {
        setCaeStatRecords(res?.data || []);
      })
      .catch(console.log)
      .finally(() => setLoadingStatCae(false));

      getSalesStats({ aaff_id: user.id, servp_ids: [2,4,5] })
      .then(res => {
        setLopdStatRecords(res?.data || []);
      })
      .catch(console.log)
      .finally(() => setLoadingStatLopd(false));
      
      getSaleCaducidad({ aaff_id: user.id })
      .then(res => {
        setCaducidadRecords(res);
        console.log(res)
      })
      .catch(console.log)
      .finally(() => setLoadingSaleCaducidad(false));

  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    setLoadingRiesgos(true);
    setLoadingSenalizaciones(true);

    getRiesgos(user.id)
      .then(res => setRiesgosData(res || []))
      .catch(console.log)
      .finally(() => setLoadingRiesgos(false));

    if (esCataluna) {
      getSenalizaciones(user.id)
        .then(res => setSenalizacionesData(res || []))
        .catch(console.log)
        .finally(() => setLoadingSenalizaciones(false));
    } else {
      setLoadingSenalizaciones(false);
    }
  }, [user?.id, i18n.language, esCataluna]);

  useEffect(() => {
    if (loadingRiesgos || riesgosData.length === 0) return;
    const top = [...riesgosData]
      .sort((a, b) => (b.totalComunidades || 0) - (a.totalComunidades || 0))
      .slice(0, 6);
    setRiesgosSeriesData(top.map(() => 0));
    const id = setTimeout(() => {
      setRiesgosSeriesData(top.map(r => r.totalComunidades || 0));
    }, 80);
    return () => clearTimeout(id);
  }, [riesgosData, loadingRiesgos]);

  useEffect(() => {
    if (loadingSenalizaciones || senalizacionesData.length === 0) return;
    const top = [...senalizacionesData]
      .sort((a, b) => (b.totalComunidades || 0) - (a.totalComunidades || 0))
      .slice(0, 6);
    setSenalesSeriesData(top.map(() => 0));
    const id = setTimeout(() => {
      setSenalesSeriesData(top.map(s => s.totalComunidades || 0));
    }, 80);
    return () => clearTimeout(id);
  }, [senalizacionesData, loadingSenalizaciones]);

  const today = new Date().toLocaleDateString(i18n.language, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const cards = [
    { label: t('home.communities'), count: fincasCount, icon: Building2, color: '#2d90f9', loading: loadingFincas, tooltip: t('home.totalCommunities') },
    { label: t('home.suppliers'), count: proveedoresCount, icon: Wrench, color: '#ffcf42', loading: loadingProveedores, tooltip: t('home.totalSuppliers') },
    {
      label: 'CAE',
      count: caeStatRecords?.total_comunidades || 0,
      icon: Boxes,
      color: '#31d583',
      loading: loadingStatCae,
      tooltip: t('home.serviceCaeInfo'),
      breakdown: [
        { label: t('home.activeCommunities'), value: caeStatRecords?.activas || 0, color: '#31d583' },
        { label: t('home.upcomingExpiry'), value: caeStatRecords?.proximas_a_caducar || 0, color: '#2d90f9' },
        { label: t('home.expiredCommunities'), value: caeStatRecords?.pdtRenovar || 0, color: '#f97066' },
        { label: t('home.droppedCommunities'), value: caeStatRecords?.baja || 0, color: '#d4d6da' },
      ],
    },
    {
      label: 'LOPD',
      count: lopdStatRecords?.total_comunidades || 0,
      icon: Boxes,
      color: '#9B59B6',
      loading: loadingStatLopd,
      tooltip: t('home.serviceLopdInfo'),
      breakdown: [
        { label: t('home.activeCommunities'), value: lopdStatRecords?.activas || 0, color: '#31d583' },
        { label: t('home.upcomingExpiry'), value: lopdStatRecords?.proximas_a_caducar || 0, color: '#2d90f9' },
        { label: t('home.expiredCommunities'), value: lopdStatRecords?.pdtRenovar || 0, color: '#f97066' },
        { label: t('home.droppedCommunities'), value: lopdStatRecords?.baja || 0, color: '#d4d6da' },
      ],
    },
  ];

  const getServicioLabel = (item) => {
    if ([1, 3, 6, 7].includes(item?.servp_id)) return t('home.serviceCae');
    if ([2, 4, 5].includes(item?.servp_id)) return t('home.serviceLopd');
    return item?.servicio || '';
  };

  return (
    <div className="px-4 pt-6 lg:px-10 lg:pt-10">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ color: '#333', fontSize: 16 }}>
            {today.charAt(0).toUpperCase() + today.slice(1)}
          </div>
          <div style={{ fontSize: 28, color: '#303248', marginTop: 6 }}>
            {t('home.greeting')} <span style={{ fontWeight: 'bold' }}>{user?.nombre || ''}</span>
          </div>
        </div>
        <img src={Logo} alt="Logo" style={{ height: 75 }} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-9">
        {cards.map((card) => (
          <div
            key={card.label}
            className="relative overflow-hidden rounded-[14px] p-3 lg:py-[22px] lg:px-6"
            style={{
              background: `linear-gradient(315deg, ${card.color}18 0%, #ffffff 60%)`,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            }}
          >
            <Tooltip title={card.tooltip} arrow placement="top" enterTouchDelay={0} leaveTouchDelay={3000}>
              <span style={{ position: 'absolute', top: 10, right: 10, cursor: 'help', display: 'flex' }}>
                <Info size={16} color="#333" />
              </span>
            </Tooltip>
            {card.loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 48 }}>
                <Loader2 className="animate-spin" size={28} color={card.color} />
              </div>
            ) : (
              <div className="lg:flex lg:items-center">
                <div className="flex items-center gap-2 lg:gap-[18px]" style={{ height: 48 }}>
                  <div
                    className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${card.color}15` }}
                  >
                    <card.icon size={22} color={card.color} strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold leading-tight" style={{ fontSize: 'clamp(20px, 5vw, 26px)', color: '#303248' }}>
                      {card.count}
                    </div>
                    <div className="truncate" style={{ fontSize: 12, color: '#333', marginTop: 2, lineHeight: 1 }}>
                      {card.label}
                    </div>
                  </div>
                </div>
                {card.breakdown && (
                  <div
                    className="mt-2 pt-2 border-t flex flex-col gap-1.5 min-w-0 lg:mt-0 lg:pt-0 lg:border-t-0 lg:pl-[14px] lg:border-l lg:ml-auto lg:self-center lg:h-12 lg:justify-between lg:gap-0 lg:relative lg:-top-2"
                    style={{ borderColor: 'rgba(0,0,0,0.06)' }}
                  >
                    {card.breakdown.map((stat) => (
                      <div key={stat.label} className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="rounded-full flex-shrink-0"
                          style={{ width: 6, height: 6, backgroundColor: stat.color }}
                        />
                        <span
                          className="inline-block text-right"
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#303248',
                            lineHeight: 1,
                            minWidth: 22,
                          }}
                        >
                          {stat.value}
                        </span>
                        <span
                          className="flex-1 truncate"
                          style={{
                            fontSize: 11,
                            color: '#333',
                            lineHeight: 1,
                          }}
                          title={stat.label}
                        >
                          {stat.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Fila Riesgos / Visitas / Señalizaciones */}
      <div className="flex flex-col lg:flex-row gap-4 mt-4">
        {/* Riesgos identificados */}
        <div className={`w-full ${colFilaRiesgos}`} style={{
          background: '#fff',
          borderRadius: 14,
          padding: '22px 24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Zap size={20} color="#f97066" strokeWidth={1.8} />
              <span style={{ fontSize: 15, fontWeight: 600, color: '#303248' }}>
                {t('home.identifiedRisks')}
              </span>
              <Tooltip title={t('home.identifiedRisksInfo')} arrow placement="top" enterTouchDelay={0} leaveTouchDelay={3000}>
                <span style={{ display: 'flex', cursor: 'help' }}>
                  <Info size={15} color="#333" />
                </span>
              </Tooltip>
            </div>
          </div>

          {/* Subtítulo */}
          <div style={{ fontSize: 13, fontWeight: 700, color: '#808080', marginBottom: 12 }}>
            {t('home.mostCommonRisks')}
          </div>

          {/* Gráfico horizontal de riesgos */}
          {loadingRiesgos ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 120 }}>
              <Loader2 className="animate-spin" size={28} color="#f97066" />
            </div>
          ) : riesgosData.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <img src={AllDone} alt="All done" style={{ width: 60 }} />
              <p className="text-sm text-gray-500 text-center">{t('home.noRisksFound')}</p>
            </div>
          ) : (() => {
            const topRiesgos = [...riesgosData]
              .sort((a, b) => (b.totalComunidades || 0) - (a.totalComunidades || 0))
              .slice(0, 6);
            const maxRiesgo = topRiesgos[0]?.totalComunidades || 1;
            const riesgoCategories = topRiesgos.map(r => r.riesgo || '');
            const series = [{
              name: t('home.communities'),
              data: riesgosSeriesData.length === topRiesgos.length
                ? riesgosSeriesData
                : topRiesgos.map(() => 0),
            }];
            return (
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', height: topRiesgos.length * 65, width: '100%' }}>
                <div style={{ width: 140, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                  {topRiesgos.map(r => (
                    <Tooltip
                      key={r.statement_id}
                      followCursor
                      enterDelay={200}
                      enterTouchDelay={0}
                      leaveTouchDelay={3000}
                      slotProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: 'rgba(30, 30, 30, 0.95)',
                            borderRadius: '10px',
                            padding: '10px 14px',
                            maxWidth: 320,
                          },
                        },
                      }}
                      title={
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600 }}>{r.riesgo}</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>
                            {r.totalComunidades} {r.totalComunidades === 1 ? t('home.community') : t('home.communities')}
                          </div>
                          {r.medidas?.length > 0 && (
                            <>
                              <div style={{ fontSize: 12, fontWeight: 600, marginTop: 6 }}>{t('home.measures')}</div>
                              <ul style={{ margin: 0, paddingLeft: 16 }}>
                                {r.medidas.map((m, i) => (
                                  <li key={i} style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', padding: '2px 0' }}>{m}</li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      }
                    >
                      <div
                        onClick={() => { setModalRisk(r); setModalOpen(true); }}
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          textAlign: 'right',
                          fontSize: 10,
                          fontWeight: 600,
                          color: '#303248',
                          paddingRight: 10,
                          cursor: 'pointer',
                          lineHeight: 1.2,
                          overflow: 'hidden',
                        }}
                      >
                        <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {r.riesgo}
                        </span>
                      </div>
                    </Tooltip>
                  ))}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                <ReactApexChart
                  type="bar"
                  height="100%"
                  width="100%"
                  series={series}
                  options={{
                    chart: {
                      toolbar: { show: false },
                      parentHeightOffset: 0,
                      sparkline: { enabled: true },
                      animations: {
                        enabled: true,
                        easing: 'easeinout',
                        speed: 900,
                        animateGradually: { enabled: true, delay: 120 },
                        dynamicAnimation: { enabled: true, speed: 400 },
                      },
                      events: {
                        dataPointSelection: (e, ctx, config) => {
                          const idx = config.dataPointIndex;
                          const risk = topRiesgos[idx];
                          if (risk) { setModalRisk(risk); setModalOpen(true); }
                        },
                      },
                    },
                    plotOptions: {
                      bar: {
                        horizontal: true,
                        borderRadius: 6,
                        borderRadiusApplication: 'end',
                        barHeight: '75%',
                        distributed: true,
                        dataLabels: { position: 'bottom' },
                      },
                    },
                    colors: ['#e54848', '#f97066', '#fb9183', '#fdb1a4', '#febfa9', '#fccfb6'],
                    dataLabels: {
                      enabled: true,
                      textAnchor: 'start',
                      offsetX: 0,
                      style: { fontSize: '12px', fontWeight: 700, colors: ['#303248'] },
                      formatter: (val) => `   ${val} ${val === 1 ? t('home.community') : t('home.communities')}`,
                    },
                    legend: { show: false },
                    grid: { borderColor: '#f0f0f0', xaxis: { lines: { show: false } }, padding: { left: 0, right: 0, top: 0, bottom: 0 } },
                    xaxis: {
                      categories: riesgoCategories,
                      min: 0,
                      max: maxRiesgo,
                      forceNiceScale: false,
                      labels: { show: false },
                      axisBorder: { show: false },
                      axisTicks: { show: false },
                    },
                    yaxis: { show: false, labels: { show: false } },
                    tooltip: { enabled: false },
                    states: { active: { filter: { type: 'none' } } },
                  }}
                />
                </div>
              </div>
            );
          })()}
        </div>

        {/* Visitas */}
        <div className={`w-full ${colFilaRiesgos}`} style={{
          background: '#fff',
          borderRadius: 14,
          padding: '22px 24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <CalendarCheck size={20} color="#2d90f9" strokeWidth={1.8} />
            <span style={{ fontSize: 15, fontWeight: 600, color: '#303248' }}>
              {t('home.serviceVisits')}
            </span>
            <Tooltip title={t('home.serviceVisitsInfo')} arrow placement="top" enterTouchDelay={0} leaveTouchDelay={3000}>
              <span style={{ display: 'flex', cursor: 'help' }}>
                <Info size={15} color="#333" />
              </span>
            </Tooltip>
          </div>

          {/* Contenido */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {(() => {
            const visitsTotal = visitsDone + visitsPending + visitsDoneBefore;
            const visitsLabels = [t('home.visitsDone'), t('home.visitsPending'), t('home.visitsDoneBefore')];
            const visitsColors = ['#31d583', '#f97066', '#06581f'];
            const visitsData = [visitsDone, visitsPending, visitsDoneBefore];
            const visitsPercents = visitsData.map(v => visitsTotal > 0 ? Math.round((v / visitsTotal) * 100) : 0);
            const visitTotalPercents = visitsPercents.filter((_, i) => i !== 1).reduce((a, b) => a + b, 0);
            if (loadingCae) {
              return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 160 }}>
                  <Loader2 className="animate-spin" size={28} color="#2d90f9" />
                </div>
              );
            }

            if (visitsTotal === 0) {
              return (
                <div className="flex flex-col items-center justify-center flex-1 gap-3 py-10 px-2">
                  <img src={Empty} alt="Empty" style={{ width: 50 }} />
                  <p className="text-sm text-gray-900 text-center">
                    {t('home.noServices', { service: t('home.serviceVisits') })}
                  </p>
                </div>
              );
            }

            return (
              <div className="flex flex-col items-center gap-4">
                {/* <Tooltip
                  followCursor
                  enterTouchDelay={0}
                  leaveTouchDelay={3000}
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'rgba(30, 30, 30, 0.95)',
                        borderRadius: '10px',
                        padding: '10px 14px',
                      },
                    },
                  }}
                  title={
                    <div style={{ padding: '4px 2px' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t('home.serviceVisits')}</div>
                      {visitsData.map((val, i) => {
                        const pct = visitsTotal > 0 ? ((val / visitsTotal) * 100).toFixed(1) : 0;
                        return (
                          <div key={visitsLabels[i]} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '3px 0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: visitsColors[i], display: 'inline-block' }} />
                              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>{visitsLabels[i]}</span>
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 600 }}>{val} ({pct}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  }
                > */}
                  <div className="w-full">
                    {/* <ReactApexChart
                      type="donut"
                      height={280}
                      series={visitsData}
                      options={{
                        labels: visitsLabels,
                        colors: visitsColors,
                        plotOptions: {
                          pie: {
                            startAngle: -90,
                            endAngle: 90,
                            offsetY: 10,
                            donut: {
                              size: '88%',
                              labels: {
                                show: true,
                                name: {
                                  show: true,
                                  offsetY: -4,
                                  fontSize: '14px',
                                  color: '#999',
                                  formatter: () => {
                                    const idx = visitsSelectedIdx.current;
                                    if (idx != null) return visitsLabels[idx];
                                    return t('home.serviceVisits');
                                  },
                                },
                                value: {
                                  show: true,
                                  offsetY: -42,
                                  fontSize: '34px',
                                  fontWeight: 700,
                                  color: '#303248',
                                  formatter: () => {
                                    const idx = visitsSelectedIdx.current;
                                    if (idx != null) return visitsData[idx];
                                    return visitsTotal;
                                  },
                                },
                                total: {
                                  show: true,
                                  showAlways: true,
                                  label: t('home.serviceVisits'),
                                  fontSize: '14px',
                                  color: '#999',
                                  formatter: () => {
                                    const idx = visitsSelectedIdx.current;
                                    if (idx != null) return visitsData[idx];
                                    return visitsTotal;
                                  },
                                },
                              },
                            },
                          },
                        },
                        dataLabels: { enabled: false },
                        legend: { show: false },
                        stroke: { width: 1 },
                        tooltip: { enabled: false },
                        grid: { padding: { bottom: -100 } },
                        states: {
                          active: { filter: { type: 'darken', value: 0.95 } },
                        },
                        chart: {
                          events: {
                            dataPointSelection: function (event, chartContext, config) {
                              const selected = config.selectedDataPoints?.[0];
                              const w = chartContext.w;
                              const valueEl = w.globals.dom.baseEl.querySelector('.apexcharts-datalabel-value');
                              const labelEl = w.globals.dom.baseEl.querySelector('.apexcharts-datalabel-label');
                              if (!valueEl || !labelEl) return;
                              if (selected && selected.length === 1) {
                                visitsSelectedIdx.current = selected[0];
                                valueEl.textContent = w.globals.series[selected[0]];
                                labelEl.textContent = w.globals.labels[selected[0]];
                              } else {
                                visitsSelectedIdx.current = null;
                                valueEl.textContent = visitsTotal;
                                labelEl.textContent = w.config.plotOptions.pie.donut.labels.total.label;
                              }
                            },
                            dataPointMouseLeave: function (event, chartContext) {
                              const idx = visitsSelectedIdx.current;
                              if (idx == null) return;
                              const w = chartContext.w;
                              const valueEl = w.globals.dom.baseEl.querySelector('.apexcharts-datalabel-value');
                              const labelEl = w.globals.dom.baseEl.querySelector('.apexcharts-datalabel-label');
                              if (!valueEl || !labelEl) return;
                              valueEl.textContent = w.globals.series[idx];
                              labelEl.textContent = w.globals.labels[idx];
                            },
                          },
                        },
                      }}
                    /> */}
                    <RadialBarMultiple
                      series={visitsPercents}
                      labels={visitsLabels}
                      colors={visitsColors}
                      total={visitTotalPercents}
                    />
                  </div>
                {/* </Tooltip> */}

                <div className="flex flex-col gap-3 w-full">
                  {visitsData.map((val, i) => {
                    const pct = visitsTotal > 0 ? ((val / visitsTotal) * 100).toFixed(1) : 0;
                    return (
                      <div key={visitsLabels[i]} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: visitsColors[i], flexShrink: 0 }} />
                          <span style={{ fontSize: 13, color: '#303248' }}>{visitsLabels[i]}</span>
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#303248', whiteSpace: 'nowrap' }}>
                          {val} <span style={{ fontWeight: 400, color: '#333' }}>({pct}%)</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          </div>
        </div>

        {/* Señalizaciones (solo AAFF de Cataluña) */}
        {esCataluna && (
        <div className="w-full lg:w-1/3" style={{
          background: '#fff',
          borderRadius: 14,
          padding: '22px 24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 9 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <SignpostBig size={20} color="#2d90f9" strokeWidth={1.8} />
              <span style={{ fontSize: 15, fontWeight: 600, color: '#303248' }}>
                {t('home.signalizations')}
              </span>
              <Tooltip title={t('home.signalizationsInfo')} arrow placement="top" enterTouchDelay={0} leaveTouchDelay={3000}>
                <span style={{ display: 'flex', cursor: 'help' }}>
                  <Info size={15} color="#333" />
                </span>
              </Tooltip>
            </div>
            <button
              onClick={() => {
                setContractType('signalization');
                setContractSubject('Contratación KAE Señaletica');
                setContractContent('Me gustaría recibir información específica para contratar este servicio.');
                setContractModalOpen(true);
              }}
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#fff',
                backgroundColor: '#222222',
                border: 'none',
                borderRadius: 8,
                padding: '6px 12px',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#111111'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#222222'}
            >
              {t('home.contractSignalization')}
            </button>
          </div>

          {/* Subtítulo */}
          <div style={{ fontSize: 13, fontWeight: 700, color: '#808080', marginBottom: 12 }}>
            {t('home.mostCommonSignals')}
          </div>

          {/* Gráfico horizontal de señalizaciones */}
          {loadingSenalizaciones ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 120 }}>
              <Loader2 className="animate-spin" size={28} color="#2d90f9" />
            </div>
          ) : senalizacionesData.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-8">
              <img src={AllDone} alt="All done" style={{ width: 60 }} />
              <p className="text-sm text-gray-500 text-center">{t('home.noSignalizationsFound')}</p>
            </div>
          ) : (() => {
            const maxSenal = topSenales[0]?.totalComunidades || 1;
            const series = [{
              name: t('home.communities'),
              data: senalesSeriesData.length === topSenales.length
                ? senalesSeriesData
                : topSenales.map(() => 0),
            }];
            return (
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', height: topSenales.length * 65, width: '100%' }}>
                <div style={{ width: 140, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                  {topSenales.map(s => (
                    <Tooltip
                      key={s.statement_id}
                      followCursor
                      enterDelay={200}
                      enterTouchDelay={0}
                      leaveTouchDelay={3000}
                      slotProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: 'rgba(30, 30, 30, 0.95)',
                            borderRadius: '10px',
                            padding: '10px 14px',
                            maxWidth: 320,
                          },
                        },
                      }}
                      title={
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600 }}>{labelFor(s)}</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>
                            {s.totalComunidades} {s.totalComunidades === 1 ? t('home.community') : t('home.communities')}
                          </div>
                        </div>
                      }
                    >
                      <div
                        onClick={() => { setModalRisk(s); setModalOpen(true); }}
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          textAlign: 'right',
                          fontSize: 10,
                          fontWeight: 600,
                          color: '#303248',
                          paddingRight: 10,
                          cursor: 'pointer',
                          lineHeight: 1.2,
                          overflow: 'hidden',
                        }}
                      >
                        <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {labelFor(s)}
                        </span>
                      </div>
                    </Tooltip>
                  ))}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ReactApexChart
                    type="bar"
                    height="100%"
                    width="100%"
                    series={series}
                    options={{
                      chart: {
                        toolbar: { show: false },
                        parentHeightOffset: 0,
                        sparkline: { enabled: true },
                        animations: {
                          enabled: true,
                          easing: 'easeinout',
                          speed: 900,
                          animateGradually: { enabled: true, delay: 120 },
                          dynamicAnimation: { enabled: true, speed: 400 },
                        },
                        events: {
                          dataPointSelection: (e, ctx, config) => {
                            const idx = config.dataPointIndex;
                            const item = topSenales[idx];
                            if (item) { setModalRisk(item); setModalOpen(true); }
                          },
                        },
                      },
                      plotOptions: {
                        bar: {
                          horizontal: true,
                          borderRadius: 6,
                          borderRadiusApplication: 'end',
                          barHeight: '75%',
                          distributed: true,
                          dataLabels: { position: 'bottom' },
                        },
                      },
                      colors: ['#1a6dbf', '#2d90f9', '#5aa9fb', '#85c0fc', '#b0d7fd', '#dceefe'],
                      dataLabels: {
                        enabled: true,
                        textAnchor: 'start',
                        offsetX: 0,
                        style: { fontSize: '12px', fontWeight: 700, colors: ['#303248'] },
                        formatter: (val) => `   ${val} ${val === 1 ? t('home.community') : t('home.communities')}`,
                      },
                      legend: { show: false },
                      grid: { borderColor: '#f0f0f0', xaxis: { lines: { show: false } }, padding: { left: 0, right: 0, top: 0, bottom: 0 } },
                      xaxis: {
                        categories: senalCategories,
                        min: 0,
                        max: maxSenal,
                        forceNiceScale: false,
                        labels: { show: false },
                        axisBorder: { show: false },
                        axisTicks: { show: false },
                      },
                      yaxis: { show: false, labels: { show: false } },
                      tooltip: { enabled: false },
                      states: { active: { filter: { type: 'none' } } },
                    }}
                  />
                </div>
              </div>
            );
          })()}
        </div>
        )}
      </div>

      {/* Fila Proveedores / Por definir */}
      <div className="flex flex-col lg:flex-row gap-4 mt-4">
        {/* Proveedores */}
        <div className="w-full lg:w-1/2" style={{
          background: '#fff',
          borderRadius: 14,
          padding: '22px 24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Wrench size={20} color="#ffcf42" strokeWidth={1.8} />
              <span style={{ fontSize: 15, fontWeight: 600, color: '#303248' }}>
                {t('home.suppliers')}
              </span>
              <Tooltip title={t('home.suppliersCardInfo')} arrow placement="top" enterTouchDelay={0} leaveTouchDelay={3000}>
                <span style={{ display: 'flex', cursor: 'help' }}>
                  <Info size={15} color="#333" />
                </span>
              </Tooltip>
            </div>
            <button
              onClick={() => navigate('/proveedores')}
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#fff',
                backgroundColor: '#222222',
                border: 'none',
                borderRadius: 8,
                padding: '6px 12px',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#111111'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#222222'}
            >
              {t('home.viewSuppliers')}
            </button>
          </div>

          {loadingProveedores ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220 }}>
              <Loader2 className="animate-spin" size={28} color="#ffcf42" />
            </div>
          ) : (<>
          {/* Progress bar section */}
          {(() => {
            const vigentes = proveedoresVigentes;
            const caducados = proveedoresCount - vigentes;
            const pctVigentes = proveedoresCount > 0 ? ((vigentes / proveedoresCount) * 100).toFixed(1) : 0;
            const pctCaducados = proveedoresCount > 0 ? ((caducados / proveedoresCount) * 100).toFixed(1) : 0;
            return (
              <div style={{ backgroundColor: '#f9f9f9', borderRadius: 10, padding: '10px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#303248', marginBottom: 10 }}>
                  {t('home.totalSuppliers')} {proveedoresCount}
                </div>
                {/* Bar */}
                <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', backgroundColor: '#f97066' }}>
                  <div style={{ width: `${pctVigentes}%`, backgroundColor: '#31d583', borderRadius: 4 }} />
                </div>
                {/* Leyenda */}
                <div className="flex flex-col sm:flex-row gap-1 sm:gap-6" style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#31d583', flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: '#666' }}>{t('home.validDocs')}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#303248' }}>{vigentes} <span style={{ fontWeight: 400, color: '#333' }}>({pctVigentes}%)</span></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#f97066', flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: '#666' }}>{t('home.expiredDocs')}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#303248' }}>{caducados} <span style={{ fontWeight: 400, color: '#333' }}>({pctCaducados}%)</span></span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Seguimiento reclamaciones */}
          <div style={{ fontSize: 13, fontWeight: 650, color: '#808080' }}>
            {t('home.claimsTracking')}
          </div>

          <div>
            {reclamaciones.map((claim, i, arr) => (
              <div key={claim.nif || claim.name}>
                <div
                  onClick={() => claim.nif && navigate(`/proveedores/${claim.nif}`)}
                  style={{ padding: '4px 0', cursor: claim.nif ? 'pointer' : 'default' }}
                >
                  <div style={{ fontSize: 13, fontWeight: 550, color: '#303248' }}>{claim.name}</div>
                  <div style={{ fontSize: 12, fontWeight: 550, color: '#898d96', marginTop: 2 }}>
                    {t('home.lastClaim')}: {claim.date}
                  </div>
                </div>
                {i < arr.length - 1 && <hr style={{ border: 'none', borderTop: '1px solid #929191', margin: 0 }} />}
              </div>
            ))}
          </div>
          </>)}
        </div>

        {/* Caducidades próximas */}
        <div className="w-full lg:w-1/2" style={{
          background: '#fff',
          borderRadius: 14,
          padding: '22px 24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <TriangleAlert size={20} color="#ffcf42" strokeWidth={1.8} />
              <span style={{ fontSize: 15, fontWeight: 600, color: '#303248' }}>
                {t('home.upcomingExpirations')}
              </span>
              <Tooltip title={t('home.expirationsInfo')} arrow placement="top" enterTouchDelay={0} leaveTouchDelay={3000}>
                <span style={{ display: 'flex', cursor: 'help' }}>
                  <Info size={15} color="#333" />
                </span>
              </Tooltip>
            </div>
            {!loadingRiesgos && caducidadRecords.length > 0 && (
              <button
                onClick={() => setCaducidadModalOpen(true)}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: '#222222',
                  border: 'none',
                  borderRadius: 8,
                  padding: '6px 12px',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#111111'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#222222'}
              >
                {t('home.seeMore')}
              </button>
            )}
          </div>
          {loadingRiesgos ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 120 }}>
              <Loader2 className="animate-spin" size={28} color="#f97066" />
            </div>
          ) : caducidadRecords.length > 0 ? (
            <div>
              {caducidadRecords.slice(0, 6).map((item, i, arr) => (
                <div key={item.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', ...(i < arr.length - 1 && { borderBottom: '1px solid #e5e5e5' }) }}>
                    <div
                      onClick={() => navigate(`/comunidades/${item.properties.nif}`)}
                      className="flex items-start gap-2 min-w-0"
                      style={{ cursor: 'pointer' }}
                    >
                      <House className="text-[#3651d8] w-5 h-5 shrink-0 mt-0.5" strokeWidth={1.5} />
                      <div className="min-w-0">
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#303248' }}>
                          {item.properties.razonSocial}
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: '#898d96', marginTop: 2 }}>
                          {getServicioLabel(item)}
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: '#898d96', whiteSpace: 'nowrap', marginLeft: 16 }}>
                      {new Date(item.saleDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                    </div>
                  </div>
                  
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center flex-1 gap-3">
              <img src={AllDone} alt="All done" style={{ width: 90 }} />
              <p className="text-base font-semibold text-gray-900">{t('home.goodJob')}</p>
              <p className="text-sm text-gray-900 text-center max-w-xs">{t('home.noRenewals')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal comunidades con riesgo */}
      <Dialog
        open={modalOpen}
        onClose={() => { setModalOpen(false); setModalRisk(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#303248' }}>
              {t('home.communitiesWithRisk')}
            </div>
            {modalRisk && (
              <div style={{ fontSize: 13, color: '#808080', marginTop: 4 }}>
                {modalRisk.riesgo || t(`home.signalizationLabels.${modalRisk.statement_id}`, { defaultValue: modalRisk.pregunta })}
              </div>
            )}
          </div>
          <IconButton size="small" onClick={() => { setModalOpen(false); setModalRisk(null); }}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {modalRisk?.medidas?.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#808080', marginBottom: 8 }}>
                {t('home.measures')}
              </div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {modalRisk.medidas.map((medida, i) => (
                  <li key={i} style={{ fontSize: 13, color: '#303248', padding: '3px 0' }}>{medida}</li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ fontSize: 13, fontWeight: 700, color: '#808080', marginBottom: 8 }}>
            {t('home.communities')} ({modalRisk?.totalComunidades || 0})
          </div>
          {modalRisk?.comunidades?.map((com, i, arr) => (
            <div key={com.id}>
              <div
                onClick={() => { setModalOpen(false); setModalRisk(null); navigate(`/comunidades/${com.nif}`); }}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 4px',
                  cursor: 'pointer',
                  borderRadius: 8,
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{ flex: 1, minWidth: 0 }} className="flex items-start gap-2">
                  <House className="text-[#3651d8] w-5 h-5 shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#303248' }}>{com.razonSocial || com.nif}</div>
                    {com.direccion && <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{com.direccion}</div>}
                  </div>
                </div>
                <span style={{ fontSize: 13, color: '#999', marginLeft: 16, whiteSpace: 'nowrap' }}>{com.nif}</span>
                <ExternalLink size={16} color="#999" style={{ marginLeft: 12, flexShrink: 0 }} />
              </div>
              {i < arr.length - 1 && <hr style={{ border: 'none', borderTop: '1px solid #e5e5e5', margin: 0 }} />}
            </div>
          ))}
        </DialogContent>
      </Dialog>

      {/* Modal contratar KAE / señalización */}
      <Dialog
        open={contractModalOpen}
        onClose={() => { logContratacionModal('cerrado'); setContractModalOpen(false); setContractSubject(''); setContractContent(''); setContractType(''); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: '#303248' }}>
            {t('home.contractSignaleticaTitle')}
          </span>
          <IconButton size="small" onClick={() => { logContratacionModal('cerrado'); setContractModalOpen(false); setContractSubject(''); setContractContent(''); setContractType(''); }}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#303248', marginBottom: 6, display: 'block' }}>
              {t('home.subject')}
            </label>
            <input
              type="text"
              value={contractSubject}
              onChange={(e) => setContractSubject(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: 14,
                border: '1px solid #ddd',
                borderRadius: 8,
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#2d90f9'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#303248', marginBottom: 6, display: 'block' }}>
              {t('home.content')}
            </label>
            <textarea
              value={contractContent}
              onChange={(e) => setContractContent(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '10px 6px',
                fontSize: 14,
                border: '1px solid #ddd',
                borderRadius: 8,
                outline: 'none',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => e.target.style.borderColor = '#2d90f9'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
          </div>
          <button
            disabled={sendingContract || !contractSubject.trim() || !contractContent.trim()}
            onClick={async () => {
              try {
                setSendingContract(true);
                await enviarEmailComercial(contractSubject, contractContent, senalizacionesConLabel);
                toast.success(t('home.emailSent'), { autoClose: 3000 });
                setContractModalOpen(false);
                setContractSubject('');
                setContractContent('');
                setContractType('');
              } catch (error) {
                console.error(error);
                toast.error(t('home.emailError'), { autoClose: 3000 });
              } finally {
                setSendingContract(false);
              }
            }}
            style={{
              marginTop: 8,
              padding: '10px 0',
              fontSize: 14,
              fontWeight: 600,
              color: '#fff',
              backgroundColor: sendingContract ? '#666' : '#222222',
              border: 'none',
              borderRadius: 8,
              cursor: sendingContract ? 'not-allowed' : 'pointer',
              opacity: (!contractSubject.trim() || !contractContent.trim()) ? 0.5 : 1,
            }}
            onMouseEnter={(e) => { if (!sendingContract) e.target.style.backgroundColor = '#111111'; }}
            onMouseLeave={(e) => { if (!sendingContract) e.target.style.backgroundColor = '#222222'; }}
          >
            {sendingContract ? <Loader2 className="animate-spin" size={18} style={{ margin: '0 auto' }} /> : t('home.getInformation')}
          </button>
        </DialogContent>
      </Dialog>

      {/* Modal caducidades completas */}
      <Dialog
        open={caducidadModalOpen}
        onClose={() => setCaducidadModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TriangleAlert size={20} color="#ffcf42" strokeWidth={1.8} />
            <span style={{ fontSize: 16, fontWeight: 600, color: '#303248' }}>
              {t('home.upcomingExpirations')}
            </span>
          </div>
          <IconButton size="small" onClick={() => setCaducidadModalOpen(false)}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#808080', marginBottom: 8 }}>
            {t('home.communities')} ({caducidadRecords.length})
          </div>
          {caducidadRecords.map((item, i, arr) => (
            <div key={item.id}>
              <div
                onClick={() => { setCaducidadModalOpen(false); navigate(`/comunidades/${item.properties.nif}`); }}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 4px',
                  cursor: 'pointer',
                  borderRadius: 8,
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{ flex: 1, minWidth: 0 }} className="flex items-start gap-2">
                  <House className="text-[#3651d8] w-5 h-5 shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#303248' }}>{item.properties.razonSocial}</div>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{getServicioLabel(item)}</div>
                  </div>
                </div>
                <span style={{ fontSize: 13, color: '#999', marginLeft: 16, whiteSpace: 'nowrap' }}>
                  {new Date(item.saleDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                </span>
              </div>
              {i < arr.length - 1 && <hr style={{ border: 'none', borderTop: '1px solid #e5e5e5', margin: 0 }} />}
            </div>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Inicio;
